var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from "react";
import { useEffect, useId, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { preferencesService } from "../../../../modules/preferences/PreferencesService";
import { getWebcamBackgroundMode } from "../../../../modules/webcam/WebcamBackgroundSettings";
import { listWebcamBackgrounds, removeWebcamBackground, saveWebcamBackground } from "../../../../modules/webcam/WebcamBackgroundStorage";
import { PreferenceNumber } from "../../preference-inputs/preference-number/PreferenceNumber.component";
import { WarningDisplay } from "../../warning-display/WarningDisplay.component";
import "./WebcamBackgroundSettings.component.css";
function BackgroundCard({ image, selected, disabled, onSelect, onDelete }) {
    const [url, setUrl] = useState("");
    const { t } = useTranslation();
    useEffect(() => {
        const objectUrl = URL.createObjectURL(image.blob);
        setUrl(objectUrl);
        return () => { URL.revokeObjectURL(objectUrl); };
    }, [image.blob]);
    return (React.createElement("div", { className: "webcam-background-card", "data-selected": selected },
        React.createElement("button", { type: "button", onClick: onSelect, disabled: disabled, "aria-pressed": selected, "aria-label": image.name, className: "webcam-background-select" },
            React.createElement("span", { className: "webcam-background-thumbnail" },
                url !== "" && React.createElement("img", { src: url, alt: "" }),
                selected && React.createElement("span", { className: "webcam-background-selected" }, t("settings:webcam-background-active", "Active"))),
            React.createElement("span", { className: "webcam-background-name", title: image.name }, image.name)),
        React.createElement("button", { type: "button", onClick: onDelete, disabled: disabled, className: "webcam-background-delete", "aria-label": t("settings:webcam-background-delete-named", "Delete {{name}}", { name: image.name }) }, t("settings:webcam-background-delete", "Delete"))));
}
export function WebcamBackgroundSettings() {
    const { t } = useTranslation();
    const id = useId();
    const uploadInput = useRef(null);
    const [images, setImages] = useState([]);
    const [mode, setMode] = useState(getWebcamBackgroundMode);
    const [selectedId, setSelectedId] = useState(() => String(preferencesService.getPreferredValue("webcamBackgroundImageId", "")));
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState("");
    const reportError = (cause) => {
        console.error("Webcam background storage failed:", cause);
        setError(cause instanceof Error ? cause.message : String(cause));
    };
    useEffect(() => {
        let disposed = false;
        void listWebcamBackgrounds().then((stored) => {
            if (!disposed)
                setImages(stored);
        }).catch((cause) => {
            if (!disposed)
                reportError(cause);
        }).finally(() => {
            if (!disposed)
                setBusy(false);
        });
        const changed = (key) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "webcamBackgroundMode" && key !== "webcamBackgroundImageId" && key !== "useWebcamBlur")
                return false;
            setMode(getWebcamBackgroundMode());
            setSelectedId(String(preferencesService.getPreferredValue("webcamBackgroundImageId", "")));
            return true;
        });
        preferencesService.onPreferenceChanged.add(changed);
        return () => {
            disposed = true;
            preferencesService.onPreferenceChanged.delete(changed);
        };
    }, []);
    const selectImage = (imageId) => {
        preferencesService.updatePreferences([
            { key: "webcamBackgroundImageId", value: imageId },
            { key: "webcamBackgroundMode", value: "image" }
        ]);
    };
    const upload = (file) => __awaiter(this, void 0, void 0, function* () {
        setBusy(true);
        setError("");
        try {
            const image = yield saveWebcamBackground(file);
            setImages((current) => [...current, image]);
            selectImage(image.id);
        }
        catch (cause) {
            reportError(cause);
        }
        finally {
            setBusy(false);
        }
    });
    const remove = (imageId) => __awaiter(this, void 0, void 0, function* () {
        setBusy(true);
        setError("");
        try {
            if (imageId === selectedId) {
                preferencesService.updatePreferences([
                    { key: "webcamBackgroundMode", value: mode === "image" ? "original" : mode },
                    { key: "webcamBackgroundImageId", value: "" }
                ]);
            }
            yield removeWebcamBackground(imageId);
            setImages((current) => current.filter((image) => image.id !== imageId));
        }
        catch (cause) {
            reportError(cause);
        }
        finally {
            setBusy(false);
        }
    });
    return (React.createElement("div", { className: "webcam-background-settings", "aria-busy": busy },
        React.createElement("label", { htmlFor: `${id}-mode` }, t("settings:webcam-background-mode", "Webcam background")),
        React.createElement("select", { id: `${id}-mode`, value: mode, disabled: busy, onChange: (event) => {
                var _a;
                const value = event.target.value;
                if (value === "image") {
                    const image = (_a = images.find((entry) => entry.id === selectedId)) !== null && _a !== void 0 ? _a : images[0];
                    if (image !== undefined)
                        selectImage(image.id);
                }
                else if (value === "original" || value === "blur") {
                    preferencesService.updatePreferences([{ key: "webcamBackgroundMode", value }]);
                }
            } },
            React.createElement("option", { value: "original" }, t("settings:webcam-background-original", "Original")),
            React.createElement("option", { value: "blur" }, t("settings:webcam-background-blur", "Blur")),
            React.createElement("option", { value: "image", disabled: images.length === 0 }, t("settings:webcam-background-image", "Custom image"))),
        mode === "blur" && (React.createElement(PreferenceNumber, { type: "slider", label: t("settings:input-webcam-blur-radius", "Background blur radius (px)"), preferenceName: "webcamBlurRadius", min: 0, max: 30, step: 1, defaultValueFallback: 9, tooltip: t("settings:input-webcam-blur-radius-tooltip", "Applied immediately when background blur is enabled") })),
        mode !== "original" && (React.createElement(React.Fragment, null,
            React.createElement(PreferenceNumber, { type: "slider", label: t("settings:input-webcam-person-threshold", "Person threshold"), preferenceName: "webcamPersonThreshold", min: 0, max: 1, step: 0.01, defaultValueFallback: 0.5, tooltip: t("settings:input-webcam-person-threshold-tooltip", "Higher values remove more background but may cut off hair and edges. Applied immediately.") }),
            React.createElement(PreferenceNumber, { type: "slider", label: t("settings:input-webcam-edge-softness", "Edge softness"), preferenceName: "webcamEdgeSoftness", min: 0, max: 1, step: 0.01, defaultValueFallback: 0.2, tooltip: t("settings:input-webcam-edge-softness-tooltip", "Controls the confidence transition around the person threshold. Zero gives hard edges. Applied immediately.") }))),
        React.createElement("section", { className: "webcam-background-library", "aria-labelledby": `${id}-library` },
            React.createElement("div", { className: "webcam-background-library-header" },
                React.createElement("h3", { id: `${id}-library` }, t("settings:webcam-background-library", "My backgrounds")),
                React.createElement("span", { className: "webcam-background-count" }, images.length)),
            images.length > 0 ? (React.createElement("div", { className: "webcam-background-gallery" }, images.map((image) => (React.createElement(BackgroundCard, { key: image.id, image: image, selected: mode === "image" && selectedId === image.id, disabled: busy, onSelect: () => { selectImage(image.id); }, onDelete: () => { void remove(image.id); } }))))) : !busy && error === "" && (React.createElement("p", { className: "webcam-background-empty" }, t("settings:webcam-background-empty", "No backgrounds yet. Add an image to personalize your webcam."))),
            React.createElement("button", { type: "button", className: "highlight-button webcam-background-upload", disabled: busy, "aria-describedby": `${id}-hint`, onClick: () => { var _a; (_a = uploadInput.current) === null || _a === void 0 ? void 0 : _a.click(); } },
                React.createElement(FontAwesomeIcon, { icon: ["fas", "folder-open"] }),
                React.createElement("span", null, t("settings:webcam-background-upload", "Add background image"))),
            React.createElement("input", { ref: uploadInput, type: "file", hidden: true, accept: "image/png,image/jpeg,image/webp", disabled: busy, "aria-describedby": `${id}-hint`, onChange: (event) => {
                    var _a;
                    const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
                    event.target.value = "";
                    if (file !== undefined)
                        void upload(file);
                } }),
            React.createElement("p", { id: `${id}-hint`, className: "webcam-background-hint" }, t("settings:webcam-background-local-hint", "Stored only in this browser. PNG, JPEG or WebP; up to 10 MB and 4096 x 4096 pixels. Images are cropped to fill the camera frame."))),
        error !== "" && (React.createElement("div", { role: "alert" },
            React.createElement(WarningDisplay, { caption: t("settings:webcam-background-storage-error", "Could not save or load webcam backgrounds") }, error)))));
}
